<!--
  =====================================================================
  NEWS SINGLE PAGE - DETAIL VIEW
  =====================================================================
  
  Displays detailed news article with dynamic content rendering.
  
  FEATURES:
  - Dynamic news lookup from URL slug
  - Multi-image carousel with navigation controls
  - Placeholder image detection (filters out placeholder images)
  - Placeholder-aware carousel rendering
  - Smooth scrolling with anchor navigation
  - Back button visibility management (hides when carousel visible)
  - Responsive image gallery with dots and arrow controls
  - Shows loading/error/not-found states
  - Formatted date display (Italian locale)
  
  URL PATTERN: /cassa/news/:slug
  
  =====================================================================
-->

<template>
  <div>
    <SiteHeader />

    <main class="news-detail">
      <div class="news-back-top" v-show="!isMegamenuOpen && !isFooterButtonVisible">
        <a href="/cassa/news" class="back-to-news">
          <span class="arrow">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          <span class="label">Torna alle news</span>
        </a>
      </div>

      <div v-if="state.loading">Caricamento...</div>
      
      <div v-else-if="state.error">Errore durante il caricamento della news</div>
      
      <div v-else>
        <div class="news-header">
          <p class="news-eyebrow" v-if="state.page.eyebrow">{{ state.page.eyebrow }}</p>
          <h1 class="news-title">{{ state.page.title }}</h1>
          <p class="news-date" v-if="state.page.date">{{ formatDate(state.page.date) }}</p>
        </div>

        <div v-if="shouldShowCarousel" class="news-image-carousel">
          <div v-if="isMultipleImages" class="carousel-container">
            <button 
              v-show="carouselIndex > 0"
              class="carousel-arrow carousel-arrow-left" 
              @click="carouselIndex--"
              aria-label="Immagine precedente"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>

            <div class="carousel-image-wrapper">
              <img 
                :src="currentCarouselImage" 
                :alt="state.page.title" 
                class="carousel-image"
              />
            </div>

            <button 
              v-show="carouselIndex < state.page.image.length - 1"
              class="carousel-arrow carousel-arrow-right" 
              @click="carouselIndex++"
              aria-label="Immagine successiva"
            >
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div v-else class="single-image-wrapper">
            <img 
              :src="currentCarouselImage" 
              :alt="state.page.title" 
              class="carousel-image"
            />
          </div>

          <div v-if="isMultipleImages" class="carousel-dots">
            <button
              v-for="(_, index) in state.page.image"
              :key="index"
              class="dot"
              :class="{ active: carouselIndex === index }"
              @click="carouselIndex = index"
              :aria-label="`Immagine ${index + 1}`"
            />
          </div>
        </div>

        <div class="news-content-wrapper">
          <component
            v-for="(section, index) in enhancedSections"
            :key="index"
            :is="getComponent(section.type)"
            :content="section.content"
          />
        </div>
      </div>
    </main>

    <ArrowUp />
    <SiteFooter />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, computed } from 'vue'
import { state, loadPageContent, setNewsPageData, setNotFound } from '@/store/index.js'

// ───────────────────────────────────────────────────────────────────
// SHARED LAYOUT COMPONENTS
// ───────────────────────────────────────────────────────────────────
import SiteHeader       from '@/components/sections/shared/SiteHeader.vue'
import SiteFooter       from '@/components/sections/shared/SiteFooter.vue'
import HtmlTextSection  from '@/components/sections/html/SectionHtmlText.vue'
import ArrowUp          from '@/components/ArrowUp.vue'

// ───────────────────────────────────────────────────────────────────
// COMPONENT STATE
// ───────────────────────────────────────────────────────────────────

const newsFound = ref(false)
const isMegamenuOpen = ref(false)
const isFooterButtonVisible = ref(false)
const carouselIndex = ref(0)

// ───────────────────────────────────────────────────────────────────
// PLACEHOLDER IMAGE DETECTION HELPERS
// ───────────────────────────────────────────────────────────────────

/**
 * Extracts filename from URL (handles absolute/relative URLs, removes query strings)
 * 
 * @param {string} src - The image source URL
 * @returns {string} The filename without path or query parameters
 */
function getBasename(src) {
  try {
    const u = new URL(src, window.location.origin)
    const cleanPath = u.pathname
    return cleanPath.substring(cleanPath.lastIndexOf('/') + 1)
  } catch (e) {
    const noQuery = String(src).split('?')[0].split('#')[0]
    const parts = noQuery.split('/')
    return parts[parts.length - 1] || noQuery
  }
}

/**
 * Regular expression to identify placeholder images
 * Matches: placeholder_news, placeholder-news, with optional _number suffix
 * Supports common image extensions: png, jpg, jpeg, webp
 */
const PLACEHOLDER_RE = /^placeholder[_-]?news(?:_\d+)?\.(png|jpg|jpeg|webp)$/i

/**
 * Determines if an image is a placeholder (used for filtering)
 * 
 * @param {string} src - The image source URL
 * @returns {boolean} True if image is identified as placeholder
 */
function isPlaceholderImage(src) {
  const name = getBasename(src)
  return PLACEHOLDER_RE.test(name)
}

// ───────────────────────────────────────────────────────────────────
// CAROUSEL IMAGE LOGIC
// ───────────────────────────────────────────────────────────────────

/**
 * Determines if carousel should show multiple images or single image display
 * @returns {boolean} True if news has 2+ images
 */
const isMultipleImages = computed(() => {
  return Array.isArray(state.page.image) && state.page.image.length > 1
})

/**
 * Smart carousel visibility control
 * Hides carousel if:
 * - No images available
 * - All images are placeholders (filtered out)
 * 
 * @returns {boolean} True if carousel should be visible
 */
const shouldShowCarousel = computed(() => {
  if (!Array.isArray(state.page.image) || state.page.image.length === 0) return false
  // Don't show if all images are placeholders
  return !state.page.image.every(img => isPlaceholderImage(img))
})

/**
 * Gets current carousel image based on navigation index
 * 
 * @returns {string|null} Current image URL or null if unavailable
 */
const currentCarouselImage = computed(() => {
  if (!Array.isArray(state.page.image) || state.page.image.length === 0) return null
  return state.page.image[carouselIndex.value] || null
})

// ───────────────────────────────────────────────────────────────────
// SECTION ENHANCEMENT WITH CAROUSEL AWARENESS
// ───────────────────────────────────────────────────────────────────

/**
 * Enhances page sections with smart image handling
 * If carousel is visible, prevents duplicate image rendering in html-text sections
 * 
 * @returns {Array} Enhanced sections with image deduplication
 */
const enhancedSections = computed(() => {
  if (!state.pageSections || state.pageSections.length === 0) return []

  const sections = [...state.pageSections]
  // Check if first section's image should be passed (carousel takes precedence)
  const hasValidImage = state.page.image && !isPlaceholderImage(state.page.image) && !shouldShowCarousel.value
  
  // Add image to first html-text section only if carousel isn't showing
  if (hasValidImage && sections.length > 0 && sections[0].type === 'html-text') {
    sections[0] = {
      ...sections[0],
      content: {
        ...sections[0].content,
        image: state.page.image
      }
    }
  }

  return sections
})

// ───────────────────────────────────────────────────────────────────
// BACK BUTTON VISIBILITY MANAGEMENT
// ───────────────────────────────────────────────────────────────────

/**
 * Checks megamenu and footer button visibility to control back button display
 * Hides back button if footer has visible "back" button to avoid redundancy
 */
const checkMegamenuAndFooter = () => {
  const megamenu = document.querySelector('.mega-menu')
  isMegamenuOpen.value = !!megamenu

  const footerButton = document.querySelector('footer .back-to-news')
  if (footerButton) {
    const rect = footerButton.getBoundingClientRect()
    isFooterButtonVisible.value = rect.top >= 0 && rect.top <= window.innerHeight
  }
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize page on component mount
 * - Extract news slug from URL
 * - Load news index from store
 * - Find and set specific news data
 * - Setup DOM observers for visibility management
 */
onMounted(async () => {
  // Parse URL: /cassa/news/:slug
  const pathSegments = window.location.pathname.split('/').filter(Boolean)
  const segments = pathSegments[0] === 'cassa' ? pathSegments.slice(1) : pathSegments
  const slug = segments[1]
  
  if (slug) {
    await loadPageContent('/news')
    newsFound.value = setNewsPageData(slug)
    
    // If news not found, trigger centralized 404 handling
    if (!newsFound.value) {
      setNotFound()
      return
    }
  }

  // Setup DOM observer for megamenu and footer visibility
  const observer = new MutationObserver(checkMegamenuAndFooter)
  observer.observe(document.body, {
    childList: true,
    attributes: true,
    attributeFilter: ['class'],
    subtree: true
  })

  // Setup scroll/resize listeners
  window.addEventListener('scroll', checkMegamenuAndFooter)
  window.addEventListener('resize', checkMegamenuAndFooter)
  setTimeout(checkMegamenuAndFooter, 100)

  // Cleanup on unmount
  onUnmounted(() => {
    observer.disconnect()
    window.removeEventListener('scroll', checkMegamenuAndFooter)
    window.removeEventListener('resize', checkMegamenuAndFooter)
  })
})

// ───────────────────────────────────────────────────────────────────
// COMPONENT RESOLVER
// ───────────────────────────────────────────────────────────────────

/**
 * Maps section content types to Vue components
 * 
 * @param {string} type - The section type identifier
 * @returns {VueComponent} The component to render
 */
function getComponent(type) {
  switch (type) {
    case 'html-text': return HtmlTextSection
  }
}

// ───────────────────────────────────────────────────────────────────
// UTILITIES
// ───────────────────────────────────────────────────────────────────

/**
 * Formats date string to Italian locale format
 * 
 * @param {string|Date} d - Date to format
 * @returns {string} Formatted date (e.g., "15 gennaio 2024")
 */
function formatDate(d) {
  return d
    ? new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
    : ''
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/partials/variables';
@import '@/assets/styles/partials/compat';
:global(.bg-news) {
  background: $clr-bg-page;
}

/* ------------------------------------------------------------------
   Wrapper
------------------------------------------------------------------ */
.news-detail {
  padding: $space-2 $container-padding-x;
  max-width: 1200px;
  margin: 0 auto;
  color: $clr-brand-gray;
}

/* Stati testuali compatti (caricamento/errore) */
.news-detail > div:not(.news-back-top):not(:has(.news-header)):not(:has(.news-content-wrapper)):not(:has(.news-not-found)) {
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-3 $container-padding-x;
  text-align: center;
  color: $text-color;
  font-size: $font-title;
  font-weight: 600;
  line-height: 1.2;
}

/* ------------------------------------------------------------------
   Bottone torna indietro
------------------------------------------------------------------ */
.news-back-top {
  position: fixed;
  top: calc(var(--header-total-height) + 1.2rem);
  left: 1rem;
  z-index: 150;

  .back-to-news {
    font-size: clamp(0.95rem, 2.5vw, 1.15rem);
    font-weight: 600;
    color: $clr-brand-dark;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    @include flex-gap(0.35rem);
    line-height: 1;
    padding: 0.6rem 0;

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;
        transform: scale(1.05);
      }
    }

    .arrow {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 1em;
      height: 1em;
      transition: transform 0.3s ease;
      transform: rotate(90deg);

      svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    }

    .label {
      display: inline-block;
      white-space: nowrap;
      transition: opacity 0.25s ease, transform 0.25s ease;
      will-change: opacity, transform;
    }

    @media (max-width: 1700px) {
      @include flex-gap(0);

      .label {
        opacity: 0;
        transform: translateX(-6px);
        pointer-events: none;
      }
    }

    @media (hover: hover) {
      &:hover .arrow {
        transform: rotate(-90deg);
      }
    }
  }

  @media (max-width: 1300px) {
    top: calc(var(--header-total-height) + 1.2rem);
    left: 1rem;
  }

  @media (max-width: 920px) {
    display: none;
  }

  @media (max-width: 768px) {
    left: 0;
  }
}

/* ------------------------------------------------------------------
   Wrapper contenuto
------------------------------------------------------------------ */
.news-content-wrapper {
}

/* ------------------------------------------------------------------
   Header (SENZA IMMAGINE)
------------------------------------------------------------------ */
.news-header {
  margin-bottom: $space-3;

  .news-eyebrow {
    position: relative;
    display: inline-block;
    font-size: $font-eyebrow;
    font-weight: 600;
    font-family: 'Karla', sans-serif!important;
    color: $clr-tertiary;
    text-transform: uppercase;
    letter-spacing: 1px;
    line-height: 1.2;
    margin: 0 0 $space-2 0;
    padding-left: $space-1-5;
    cursor: default;
    white-space: nowrap;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -30px;
      width: 40%;
      height: 12px;
      background-color: rgba($clr-tertiary, 0.7);
      transition: all 0.5s ease;
    }

    @media (hover: hover) {
      &:hover {
        color: $clr-tertiary;

        &::before {
          background: rgba($clr-tertiary, 0.8);
          width: 120%;
          transform: scaleY(1.2);
        }
      }
    }
  }

  .news-title {
    font-size: $font-title;
    font-weight: 300;
    font-family: 'Karla', sans-serif!important;
    color: $clr-brand-gray;
    line-height: 1.2;
    margin: 0 0 $space-0-25 0;
  }

  .news-date {
    font-size: $font-caption;
    font-family: 'Nunito', sans-serif!important;
    color: $clr-brand-gray;
    margin: $space-0-5 0 $space-2 0;
    padding-left: $space-2;
  }
}

/* ------------------------------------------------------------------
   News not found
------------------------------------------------------------------ */
.news-not-found {
  min-height: calc(100vh - var(--header-total-height) - var(--footer-total-height));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  @include flex-gap($space-0-5, column);
  padding: $space-3 $container-padding-x;

  h1 {
    font-size: $font-title;
    color: $clr-brand-dark;
    margin: 0;
    font-weight: 600;
    line-height: 1.2;
  }

  p {
    font-size: $font-lg;
    color: $clr-text-light;
    margin: 0;
    line-height: 1.35;
  }

  .back-to-news {
    margin-top: $space-1;
  }
}

.news-not-found .back-to-news {
  font-size: clamp(0.95rem, 2.5vw, 1.15rem);
  font-weight: 600;
  color: $clr-brand-dark;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  @include flex-gap(0.35rem);
  line-height: 1;
  transition: all 0.25s ease;
  padding: 0.6rem 0;

  .arrow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1em;
    transition: transform 0.3s ease;
    transform: rotate(90deg);

    svg {
      width: 100%;
      height: 100%;
      display: block;
    }
  }

  .label {
    display: inline-block;
    white-space: nowrap;
    transition: opacity 0.25s ease, transform 0.25s ease;
    will-change: opacity, transform;
  }

  @media (max-width: 1500px) {
    @include flex-gap(0);

    .label {
      opacity: 0;
      transform: translateX(-6px);
      pointer-events: none;
    }
  }

  @media (hover: hover) {
    &:hover {
      color: $clr-tertiary;
      transform: translateX(-4px);
    }

    &:hover .arrow {
      transform: rotate(-90deg);
    }
  }
}

/* ------------------------------------------------------------------
   Media queries - Display medio-grande intermedio (1025px – 1300px)
   Necessario perché main { padding: 0 !important } nel global
   sovrascrive il padding del componente; qui usiamo !important con
   specificità maggiore (selettore di classe) per ripristinarlo.
------------------------------------------------------------------ */
@media (min-width: 1025px) and (max-width: 1300px) {
  .news-detail {
    padding-left: clamp(2rem, 4vw, 3rem) !important;
    padding-right: clamp(2rem, 4vw, 3rem) !important;
  }
}

/* ------------------------------------------------------------------
   Media queries - Display medio-grande (≤1300px)
------------------------------------------------------------------ */
@media (max-width: 1300px) {
  .news-header {
    .news-eyebrow {
      font-size: $font-eyebrow-lg;

      &::before {
        height: 10px;
        left: -25px;
      }
    }

    .news-title {
      font-size: $font-title-lg;
      font-weight: 300;
    }
  }
}

/* ------------------------------------------------------------------
   Tablet / Mobile (≤1024px) — padding laterale con !important
   necessario perché body main { padding: 0 !important } nel global
   sovrascrive il padding del componente.
------------------------------------------------------------------ */
@media (max-width: 1024px) {
  .news-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
    overflow-x: hidden;
  }

  /* Immagine a tutta larghezza (bleed) — bilancia esattamente il padding */
  .news-image-carousel {
    margin-left: -$space-1;
    margin-right: -$space-1;
    width: calc(100% + 2rem);
  }

  .carousel-image {
    width: 100%;
    max-height: 300px;
    box-shadow: none;
  }

  .carousel-arrow {
    &.carousel-arrow-left { left: 0.75rem; }
    &.carousel-arrow-right { right: 0.75rem; }
  }
}

/* ------------------------------------------------------------------
   Tablet (≤920px)
------------------------------------------------------------------ */
@media (max-width: 920px) {
  .news-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }

  .news-header {
    .news-eyebrow {
      font-size: $font-eyebrow-sm;
      margin-bottom: $space-1;
      white-space: normal;

      &::before {
        height: 8px;
        left: -20px;
      }
    }

    .news-title {
      font-size: $font-title-sm;
      font-weight: 300;
    }
  }
}

/* ------------------------------------------------------------------
   Mobile (≤ 768 px)
------------------------------------------------------------------ */
@media (max-width: 768px) {
  .news-detail {
    padding-left: $space-1 !important;
    padding-right: $space-1 !important;
  }
}

/* ------------------------------------------------------------------
   Mobile (≤480px)
------------------------------------------------------------------ */
@media (max-width: 480px) {
  .news-header {
    .news-eyebrow {
      font-size: $font-eyebrow-xs;
      margin-bottom: $space-0-5;

      &::before {
        height: 7px;
        left: -15px;
      }
    }

    .news-title {
      font-size: $font-title-xs;
      font-weight: 300;
    }
  }
}

/* ------------------------------------------------------------------
   Mobile molto piccolo (≤360px)
------------------------------------------------------------------ */
@media (max-width: 360px) {
  .news-header {
    .news-eyebrow {
      font-size: $font-eyebrow-xxs;

      &::before {
        height: 6px;
        left: -10px;
      }
    }

    .news-title {
      font-size: $font-title-xxs;
      font-weight: 300;
    }
  }
}

/* ------------------------------------------------------------------
   Carosello Immagini
------------------------------------------------------------------ */
.news-image-carousel {
  width: 100%;
  margin: 0 auto $space-3 auto;
  text-align: center;
}

.carousel-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  @include flex-gap(1rem);
}

.carousel-image-wrapper,
.single-image-wrapper {
  width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.carousel-image {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 500px;
  display: inline-block;
  box-shadow: 0 4px 16px rgba($clr-brand-dark, 0.1);
  transition: all 0.3s ease;

  @media (min-width: 1025px) and (max-width: 1300px) {
    max-height: 360px;
  }

  @media (hover: hover) {
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 8px 24px rgba($clr-brand-dark, 0.15);
    }
  }
}

.carousel-arrow {
  position: absolute;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba($clr-brand-dark, 0.1);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  flex-shrink: 0;
  outline: none;
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
    color: $clr-brand-dark;
  }

  @media (hover: hover) {
    &:hover {
      background-color: rgba(255, 255, 255, 1);
      transform: scale(1.1);
      box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.15);
    }
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }
  /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
     scartata per intero. Si ripiega su :focus, che mostra l'anello
     anche col mouse: e' il degrado accettato. */
  .no-focusvisible &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
  }

  &:active {
    transform: scale(0.98);
  }

  &.carousel-arrow-left {
    left: -3rem;
  }

  &.carousel-arrow-right {
    right: -3rem;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    padding: 0;

    svg {
      width: 20px;
      height: 20px;
    }

    &.carousel-arrow-left {
      left: 0.75rem;
    }

    &.carousel-arrow-right {
      right: 0.75rem;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
  }

  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
    padding: 0;

    svg {
      width: 18px;
      height: 18px;
    }

    &.carousel-arrow-left {
      left: 0.5rem;
    }

    &.carousel-arrow-right {
      right: 0.5rem;
    }

    &:focus,
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
    /* ricaduta iOS < 15.4: senza :focus-visible la regola sopra viene
       scartata per intero. Si ripiega su :focus, che mostra l'anello
       anche col mouse: e' il degrado accettato. */
    .no-focusvisible &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($clr-tertiary, 0.3);
    }
  }
}

.carousel-dots {
  display: flex;
  justify-content: center;
  @include flex-gap(0.75rem);
  margin-top: 1.5rem;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background-color: rgba($clr-text-light, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;

  @media (hover: hover) {
    &:hover {
      background-color: rgba($clr-text-light, 0.5);
      transform: scale(1.2);
    }
  }

  &.active {
    background-color: $clr-tertiary;
    width: 24px;
    border-radius: 5px;
  }
}

/* ──────────────────────────────────────────────────────────────
   L'eyebrow aveva un padding-left che spostava il TESTO a destra
   lasciando il box dov'era: era quello a disallinearlo dal titolo.
   Qui viene azzerato, cosi' l'eyebrow parte esattamente dove parte
   il titolo. Nessun titolo viene spostato.
   Il ::before e' position:absolute e non dipende dal padding.
   Classe ripetuta tre volte per battere le regole annidate originali.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .news-eyebrow.news-eyebrow.news-eyebrow {
    padding-left: 0;
  }
}

/* ──────────────────────────────────────────────────────────────
   ALLINEAMENTO AL PARAGRAFO
   eyebrow, titolo e corpo del testo devono partire dalla stessa x,
   quella di "chi siamo": 44px a 390, 39 a 360, 54 a 600, 102 a 768-920,
   122 a 1024. Valori ricavati misurando il primo glifo di ogni elemento.
   Classe ripetuta tre volte per battere le regole annidate originali;
   blocchi in fondo al file perche' l'ordine decide fra breakpoint.
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .news-eyebrow.news-eyebrow.news-eyebrow {
    padding-left: 0;
  }
}

@media (max-width: 480px) {
  .news-eyebrow.news-eyebrow.news-eyebrow {
    margin-left: 28px;
    padding-left: 0;
  }

  .news-title.news-title.news-title {
    margin-left: 28px;
  }

  :deep(.html-content) {
    margin-left: 12px;
  }
}

@media (max-width: 360px) {
  .news-eyebrow.news-eyebrow.news-eyebrow {
    margin-left: 23px;
    padding-left: 0;
  }

  .news-title.news-title.news-title {
    margin-left: 23px;
  }

  :deep(.html-content) {
    margin-left: 11px;
  }
}
</style>
